import { HeroIcon } from '@components/HeroIcon'
import { Button } from '@components/Button'
import { useTranslation } from 'react-i18next'
import { H3, Stack, Text, XStack, YStack } from 'tamagui'
import { AuthEmailModal } from '@components/AuthEmailModal'
import { useState } from 'react'
import { CancelSubscriptionModal } from './CancelSubscriptionModal'
import { DownsellOfferModal } from './DownsellOfferModal'
import { UnsubscribeSuccessModal } from './UnsubscribeSuccessModal'
import { cancelSubscription } from '@services/api/order'
import {
  acceptSubscriptionDownsell,
  declineSubscriptionDownsell,
} from '@services/api/downsell'
import { useSubscriptionDownsellQuery } from '@queries/downsell'
import { useToastController } from '@tamagui/toast'
import { useOrderDetails } from '@contexts/OrderDetailsContext'
import { useSession } from '@hooks/useSession'
import { useLocalSearchParams } from 'expo-router'
import { useRudderstack } from '@hooks/useAnalytics'
import {
  SUBSCRIPTION_ACTIVE_STATUS,
  SUBSCRIPTION_PAST_DUE_STATUS,
} from '@constants/subscription'
import { DOWNSELL } from '@constants/features'
import { useHasFeature, useThemeParams } from '@hooks/useThemeParams'
import { formatMoney } from '@utils/moneyFormatter'
import { formatDateShort } from '@utils/dateFormatter'

export function Subscription() {
  const { data: params } = useThemeParams()
  const { action } = useLocalSearchParams<{ action?: string }>()
  const isChangeCreditCardModalOpen = action === 'cancelSubscription'
  const { t } = useTranslation('OrderDetails')
  const [openLoginModal, setOpenLoginModal] = useState(false)
  const [cancelModal, setCancelModal] = useState(isChangeCreditCardModalOpen)
  const [successCancelModal, setSuccessCancelModal] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [downsellModal, setDownsellModal] = useState(false)
  const [downsellAccepted, setDownsellAccepted] = useState(false)
  const [downsellAccepting, setDownsellAccepting] = useState(false)
  const [downsellError, setDownsellError] = useState(false)
  const toast = useToastController()
  const { logged } = useSession()

  const {
    isSubscription,
    relevantPayment,
    subscription,
    productTitle,
    createdAt,
    id,
    refetch,
  } = useOrderDetails()

  const { track } = useRudderstack()
  const hasDownsellFeature = useHasFeature(DOWNSELL)

  const allowCancelStatus = [
    SUBSCRIPTION_ACTIVE_STATUS,
    SUBSCRIPTION_PAST_DUE_STATUS,
  ].includes(subscription?.status ?? '')

  // Pré-carregada para o modal abrir instantâneo. Erro/inelegível => null e o
  // cancelamento segue direto (fail-open: nada pode impedir o aluno de cancelar).
  const { data: downsell } = useSubscriptionDownsellQuery(
    id,
    Boolean(
      hasDownsellFeature && isSubscription && allowCancelStatus && logged,
    ),
  )

  const downsellTrackProps = {
    product_name: productTitle,
    subscription_id: subscription?.id,
    subscription_status: subscription?.status,
    cart_id: id,
    school_id: params?.school?.id,
    downsell_id: downsell?.id,
    new_price_cents: downsell?.newPriceCents,
  }

  async function handleDownsellAccept() {
    setDownsellAccepting(true)
    setDownsellError(false)

    try {
      await acceptSubscriptionDownsell(id)
      setDownsellAccepted(true)
      track('Subscription Downsell Accepted', downsellTrackProps)
    } catch (error) {
      // Aceite falhou: assinatura intocada, one-shot NÃO queimado; retry no
      // modal. Nunca cair no cancelamento por engano.
      setDownsellError(true)
      console.error(error)
    } finally {
      setDownsellAccepting(false)
    }
  }

  function handleDownsellDecline() {
    // Fire-and-confirm: a recusa queima server-side, mas a UI segue para o
    // confirm de cancelamento mesmo se o POST falhar (o backend também
    // considera cancelamento concluído como queima).
    declineSubscriptionDownsell(id).catch(console.error)
    track('Subscription Downsell Declined', downsellTrackProps)
    setDownsellModal(false)
    setCancelModal(true)
  }

  function handleDownsellClose() {
    // Fechar não queima e não cancela (decisão de produto).
    setDownsellModal(false)
    if (downsellAccepted) refetch()
  }

  async function handleSubscriptionCancel() {
    setCancelling(true)

    try {
      await cancelSubscription(id)
      await refetch()

      setCancelModal(false)
      setSuccessCancelModal(true)
      track('Subscription Cancelled by Student', {
        product_name: productTitle,
        subscription_id: subscription?.id,
        subscription_status: subscription?.status,
        subscription_type: subscription?.subscription_type,
        cart_id: id,
        cart_created_at: createdAt,
        school_id: params?.school?.id,
      })
    } catch (error) {
      setCancelModal(false)
      toast.show(t('subscription.toast.title'), {
        message: t('subscription.toast.description'),
        type: 'error',
      })
      console.error(error)
    } finally {
      setCancelling(false)
    }
  }

  function handleCancel() {
    if (!logged) return setOpenLoginModal(true)

    // Variante A: downsell ANTES do confirm. Elegível e ainda não aceito =>
    // intercepta; qualquer outro caso segue o fluxo atual.
    if (downsell && !downsellAccepted) {
      setDownsellModal(true)
      track('Subscription Downsell Viewed', downsellTrackProps)
      return
    }

    setCancelModal(true)
  }

  if (!isSubscription || !subscription) return null

  const allowCancelSubscription = allowCancelStatus

  // Durante o período de teste o pagamento existente é a validação de R$ 0,00 — o valor real da
  // assinatura vem da própria subscription, não do pagamento.
  const inTrial = Boolean(subscription.in_trial)
  const subscriptionAmount =
    inTrial && subscription.amount
      ? formatMoney(subscription.amount)
      : relevantPayment.amount
  const trialEndsAt = subscription.trial_ends_at
    ? formatDateShort(subscription.trial_ends_at)
    : subscription.nextInvoiceAt

  return (
    <YStack gap="$size.spacing-xs">
      <H3 color="$text-primary" ff="$mulishBold" fontSize="$font-size-md">
        {t('subscription.title')}
      </H3>
      <Stack
        p="$size.spacing-xs"
        br="$border-radius-sm"
        borderWidth="$borderWidth.border-width-hairline"
        borderColor="$border-strong"
        rowGap="$size.spacing-xxs"
        $gtSm={{
          flexDirection: 'row',
          padding: '$size.spacing-md',
          columnGap: '$size.spacing-lg',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <Stack
          rowGap="$size.spacing-xxs"
          flexWrap="wrap"
          $gtSm={{
            flexDirection: 'row',
            columnGap: '$size.spacing-lg',
          }}
        >
          <XStack gap="$size.spacing-xxs">
            <XStack gap="$size.spacing-xxxs" ai="center">
              <HeroIcon
                icon={['fal', 'badge-dollar']}
                color="$icon-primary"
                size={24}
              />
              <Text
                color="$text-primary"
                ff="$mulishRegular"
                fontSize="$font-size-xxs"
              >
                {t('subscription.amount')}:{' '}
                <Text ff="$mulishBold" color="$text-primary">
                  {subscriptionAmount}
                </Text>
              </Text>
            </XStack>
          </XStack>
          {inTrial && (
            <XStack gap="$size.spacing-xxs">
              <XStack gap="$size.spacing-xxxs" ai="center">
                <HeroIcon
                  icon={['fal', 'hourglass']}
                  color="$icon-primary"
                  size={24}
                />
                <Text
                  color="$text-primary"
                  ff="$mulishRegular"
                  fontSize="$font-size-xxs"
                  testID="subscription-trial-info"
                >
                  {t('subscription.trial')}:{' '}
                  <Text ff="$mulishBold" color="$text-primary">
                    {trialEndsAt}
                  </Text>
                </Text>
              </XStack>
            </XStack>
          )}
          {allowCancelSubscription && (
            <XStack gap="$size.spacing-xxs">
              <XStack gap="$size.spacing-xxxs" ai="center">
                <HeroIcon
                  icon={['fal', 'calendar-alt']}
                  color="$icon-primary"
                  size={24}
                />
                <Text
                  color="$text-primary"
                  ff="$mulishRegular"
                  fontSize="$font-size-xxs"
                >
                  {t('subscription.bill')}:{' '}
                  <Text ff="$mulishBold" color="$text-primary">
                    {subscription.nextInvoiceAt}
                  </Text>
                </Text>
              </XStack>
            </XStack>
          )}
        </Stack>
        {allowCancelSubscription && (
          <Button loading={cancelling} variant="danger" onPress={handleCancel}>
            {t('subscription.cancel')}
          </Button>
        )}
        <AuthEmailModal
          onClose={() => setOpenLoginModal(false)}
          open={openLoginModal}
          orderInfo={{
            id,
            subscription,
            productTitle,
            createdAt,
          }}
          action="cancelSubscription"
        />
        <DownsellOfferModal
          open={downsellModal}
          downsell={downsell ?? null}
          accepting={downsellAccepting}
          accepted={downsellAccepted}
          hasError={downsellError}
          onAccept={handleDownsellAccept}
          onDecline={handleDownsellDecline}
          onClose={handleDownsellClose}
          courseName={productTitle}
          cardMaskedNumber={subscription.credit_card_masked_number}
        />
        <CancelSubscriptionModal
          open={cancelModal}
          onClose={() => setCancelModal(false)}
          onConfirm={handleSubscriptionCancel}
          courseName={productTitle}
        />
        <UnsubscribeSuccessModal
          open={successCancelModal}
          onClose={() => setSuccessCancelModal(false)}
          courseName={productTitle}
          expirationDate={subscription?.nextInvoiceAt}
        />
      </Stack>
    </YStack>
  )
}
